/**
 * controller init function for WixComments component
 * @module controller
 */
import { mapValues, omitBy } from 'lodash';
import { setBaseParams, updateInstance } from '../common/store/base-params/base-params-actions';
import { fetchTranslations } from '../common/store/translations/translations-actions';
import { validateLanguage } from './utils/validate-language';
import { createInstanceManager } from './utils/get-instance';
import { fetchExperiments } from '../common/store/experiments/experiments-actions';
import { refetchCommentsData } from '../common/store/comments/paging/comments-paging-actions';
import { createSentryMonitor } from './utils/monitoring';
import { handleError } from './utils/handle-error';
import { getIsMemberCheckPending } from '../common/store/members/members-selectors';
import { initMaNavigation } from '../common/store/ma-navigation/ma-navigation-actions';
import { initDevToolsStore } from '../component/components/dev-panel/init-dev-tool-store';
import { createStateOptimizer } from './utils/state-optimizer';
import { createCommentsWatcherConfig } from '../common/store/comments/created-comments-watcher';
import { initBeforeCrudInterceptor } from '../common/store/comments/comments-before-crud-interceptor';
import { resolveTestMode } from './utils/resolve-test-mode';
import { createCommentsStore } from '../common/store/create-comments-store';
import { createStateCache } from './create-state-cache';
import { initControllerActions } from './init-controller-actions';
import { createCommentsControllerApi } from './create-comments-controller-api';
export * from './controller-public-types';
export { Ordering, SortOrder } from '../common/common-enums';
/**
 * @param controllerConfig - provided by the platform: wixCodeApi, platformAPIs, setProps, compId. Pass `controllerContex.flowApi.contorllerConfig` from your controller paramters
 * @param options - configure comments controller
 *
 *  @example
 *  ```typescript
 * // initialize the controller inside your worker
 * import { initializeCommentsController } from '@wix/comments-ooi-client/controller';
 *
 * async pageReady() {
 *   const commentsApi = await initializeCommentsController(flowAPI.controllerConfig, {
 *     appDefinitionId: 'YOUR-APP-DEF-ID',
 *   });
 * }
 *```
 * After controller is initialized it returns API that can be used for advanced functionality.
 */
export async function initializeCommentsController(controllerConfig, { appDefinitionId, shouldAutoBindStateToSetProps = true, actionPreconditions, liveUpdates, testMode: inputTestMode, logNetworkErrorsInConsole = false, httpClient, errorHandler, mocks, _persistDraft, enableWarmupDataCache, }) {
    const { wixCodeApi, platformAPIs, setProps, compId } = controllerConfig;
    const isSSR = wixCodeApi.window.rendering.env === 'backend';
    const viewMode = wixCodeApi.window.viewMode;
    const sentry = createSentryMonitor({ isSSR, platformAPIs });
    let store;
    let actions;
    const stateCache = createStateCache({ wixCodeApi });
    const testMode = resolveTestMode(inputTestMode);
    const invokeAppCrash = () => {
        const dummyError = new Error(DUMMY_ERROR);
        handleError({ store, isSSR, setProps, actions, sentry })(dummyError);
    };
    const isDebug = !!wixCodeApi.location.query.debug;
    const devToolsApi = initDevToolsStore({
        onChange: (state) => {
            if (state.specialStates === 'crash') {
                invokeAppCrash();
            }
            setProps({ wcDevToolsState: state });
        },
        wixCodeApi,
        isDebug,
    });
    if (devToolsApi.getState().specialStates === 'crash') {
        invokeAppCrash();
    }
    const commentsWatcherConfig = createCommentsWatcherConfig();
    const beforeCreateInterceptor = initBeforeCrudInterceptor();
    try {
        if (isSSR && testMode.crashIn === 'SSR') {
            throw new Error('Damn, unlucky crash!');
        }
        const language = validateLanguage(devToolsApi.getState().language ||
            (wixCodeApi.window.multilingual.isEnabled &&
                wixCodeApi.window?.multilingual?.currentLanguage) ||
            wixCodeApi.site.language);
        const instanceManager = createInstanceManager(wixCodeApi, appDefinitionId);
        store = createCommentsStore({
            wixCodeApi,
            platformAPIs,
            appDefinitionId,
            compId,
            actionPreconditions,
            isSSR,
            duplexerClient: liveUpdates?.duplexerClient,
            instanceManager,
            sentry,
            devToolsApi,
            testMode,
            commentsWatcherMiddleware: commentsWatcherConfig.middleware,
            executeBeforeCrud: beforeCreateInterceptor.executeBeforeCrud,
            logNetworkErrorsInConsole,
            httpClient,
            errorHandler,
            persistDraft: _persistDraft,
            mocks,
            preloadedState: !!enableWarmupDataCache && !isSSR ? stateCache.getState() : undefined,
        });
        // handle app token refresh
        instanceManager.onChange(({ instance }) => {
            store?.dispatch(updateInstance({ instance }));
        });
        await Promise.all([
            store.dispatch(fetchTranslations(language)),
            initUserDependentStoreBaseData({
                store,
                appDefinitionId,
                language,
                isSSR,
            }),
        ]);
        actions = initControllerActions({
            wixCodeApi,
            store,
            sentry,
            logErrorsToConsole: isDebug || logNetworkErrorsInConsole,
        });
        refreshDataOnLogin({
            wixCodeApi,
            store,
            appDefinitionId,
            language,
        });
    }
    catch (error) {
        handleError({ store, isSSR, setProps, actions, sentry })(error);
    }
    const bindStateToSetProps = () => {
        if (store && actions) {
            if (isSSR) {
                const successFlags = mapValues(store.getState().comments, (resourceState) => resourceState?.type === 'READY');
                wixCodeApi.window.warmupData.set('SSR_FETCH_MODE', 'comments_only');
                wixCodeApi.window.warmupData.set('WC_SSR_LOAD', successFlags);
            }
            const stateOptimizer = createStateOptimizer({
                store,
                onChange: (wcState) => setProps({ wcState }),
                disablePatching: isSSR || viewMode === 'Editor',
            });
            if (enableWarmupDataCache && isSSR) {
                const state = store.getState();
                stateCache.setState({
                    ...state,
                    comments: omitBy(state.comments, (resourceState) => resourceState?.type !== 'READY'),
                });
            }
            setProps({
                wcState: {
                    type: 'SET',
                    state: store.getState(),
                },
                ...actions,
                wcDevToolsApi: devToolsApi,
                wcDevToolsState: devToolsApi.getState(),
                wcRequestFullState: stateOptimizer.requestFullState,
                wcSsrLoadedCorrectly: isSSR
                    ? mapValues(store.getState().comments, () => true)
                    : wixCodeApi.window.warmupData.get('WC_SSR_LOAD'),
            });
            stateOptimizer.subscribe();
        }
        else {
            // The error should already be logged by 'handleError' above
        }
    };
    if (shouldAutoBindStateToSetProps) {
        bindStateToSetProps();
    }
    const commentsApi = createCommentsControllerApi({
        actions,
        devToolsApi,
        beforeCreateInterceptor,
        store,
        bindStateToSetProps,
        shouldAutoBindStateToSetProps,
        commentsWatcherConfig,
    });
    return commentsApi;
}
function initUserDependentStoreBaseData({ store, appDefinitionId, language, isSSR = false, }) {
    return Promise.all([
        store.dispatch(setBaseParams({ appDefinitionId, language, isSSR })).then(() => {
            store.dispatch(fetchExperiments());
            store.dispatch(initMaNavigation());
        }),
    ]);
}
function refreshDataOnLogin({ wixCodeApi, store, appDefinitionId, language, }) {
    wixCodeApi.user.onLogin(() => {
        setTimeout(() => {
            initUserDependentStoreBaseData({ store, appDefinitionId, language });
            !getIsMemberCheckPending(store.getState()) && store.dispatch(refetchCommentsData());
        });
    });
}
/** @internal */
export const DUMMY_ERROR = 'This is a dummy error';
//# sourceMappingURL=controller.js.map